/**
 * Product Service
 */
import apiClient from './apiClient';

export interface Product {
  id: number;
  codigo: string;
  gtin?: string;
  ean?: string;  // EAN code from ProductoCliente
  nome: string;
  descricao?: string;
  categoria?: string;
  fabricante?: string;
  preco_unitario?: number;  // Optional because ProductoCliente doesn't have price
  preco?: number;  // Alternative price field from ProductoCliente endpoint
  unidade?: string;
  ativo?: boolean;
  cliente_id?: number;
  cliente_razao_social?: string;
  cliente_nome_fantasia?: string;

  // Campos estendidos (Excel/SAP)
  planta?: string;
  lote?: string;
  data_validade?: string; // ISO date string
  dias_para_vencer?: number;
  grupo_preco?: string;
  codigo_cliente?: string;
  razao_social_cliente?: string;
  quantidade_consignada?: number;
  cidade?: string;
  cep?: string;
  regiao?: string;

  // Campos GS1 (Códigos de Barras)
  gtin_14?: string;
  tipo_codigo_base?: string; // 'EAN-13' | 'ITF-14'

  // GTIN-S-004: Array de GTINs cadastrados (da tabela ProductoClienteGTIN)
  gtins?: Array<{
    gtin: string;
    tipo: string;
    fator: number;
  }>;
}

/**
 * Resultado da busca inteligente por código de barras GS1
 */
export interface BarcodeSearchResult {
  product: Product;
  lote?: string | null;
  data_validade?: string | null;
  numero_serie?: string | null;
  tipo_codigo: string; // 'DataMatrix' | 'GS1-128' | 'ITF-14' | 'EAN-13'
  codigo_completo: string;
}

export interface ProductCreateInput {
  codigo: string;
  gtin?: string;
  nome: string;
  descricao?: string;
  categoria?: string;
  fabricante?: string;
  preco_unitario: number;
  unidade?: string;

  // Campos estendidos (Excel/SAP)
  planta?: string;
  lote?: string;
  data_validade?: string; // ISO date string
  grupo_preco?: string;
  codigo_cliente?: string;
  nome_cliente?: string;
  quantidade_consignada?: number;
  cidade?: string;
  cep?: string;
  regiao?: string;
}

/**
 * Get all products (from Product table - dim_produtos)
 */
export const getProducts = async (): Promise<Product[]> => {
  try {
    const response = await apiClient.get<Product[]>('/products');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching products:', error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch products');
  }
};

/**
 * Get products from client catalog (ProductoCliente table - pt_productos_cliente)
 * Used by the barcode page to display products with GTIN/EAN codes
 */
export const getProductsCliente = async (): Promise<Product[]> => {
  try {
    const response = await apiClient.get<Product[]>('/products-cliente');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching products cliente:', error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch client products');
  }
};

/**
 * Get product by ID
 */
export const getProductById = async (id: number): Promise<Product> => {
  try {
    const response = await apiClient.get<Product>(`/products/${id}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching product ${id}:`, error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch product');
  }
};

/**
 * Create new product
 */
export const createProduct = async (data: ProductCreateInput): Promise<Product> => {
  try {
    const response = await apiClient.post<Product>('/products', data);
    return response.data;
  } catch (error: any) {
    console.error('Error creating product:', error);
    throw new Error(error.response?.data?.detail || 'Failed to create product');
  }
};

/**
 * Update product
 */
export const updateProduct = async (id: number, data: ProductCreateInput): Promise<Product> => {
  try {
    const response = await apiClient.put<Product>(`/products/${id}`, data);
    return response.data;
  } catch (error: any) {
    console.error(`Error updating product ${id}:`, error);
    throw new Error(error.response?.data?.detail || 'Failed to update product');
  }
};

/**
 * Delete product (soft delete)
 */
export const deleteProduct = async (id: number): Promise<{success: boolean; message: string}> => {
  try {
    const response = await apiClient.delete<{success: boolean; message: string}>(`/products/${id}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error deleting product ${id}:`, error);
    throw new Error(error.response?.data?.detail || 'Failed to delete product');
  }
};

/**
 * 🔍 BUSCA INTELIGENTE GS1 - Decodifica códigos de barras e extrai dados
 *
 * Aceita 4 tipos de códigos:
 * - 📦 DataMatrix (unidade): (01)GTIN(17)YYMMDD(10)LOTE(21)SÉRIE
 *   Exemplo: (01)07891234567890(17)260630(10)L1234(21)SN00001
 *   Retorna: produto + lote + validade + série
 *
 * - 📦 GS1-128 (caixa): (01)GTIN(17)YYMMDD(10)LOTE
 *   Exemplo: (01)07891234567890(17)260630(10)L1234
 *   Retorna: produto + lote + validade
 *
 * - 📦 ITF-14 (pallet): 14 dígitos GTIN-14
 *   Exemplo: 17891234567893
 *   Retorna: apenas produto
 *
 * - 📦 EAN-13 (básico): 13 dígitos GTIN
 *   Exemplo: 7891234567895
 *   Retorna: apenas produto
 *
 * @param barcode - Código de barras escaneado (qualquer formato GS1)
 * @returns BarcodeSearchResult com produto + dados extraídos (lote/validade/série quando disponível)
 */
export const searchProductByBarcode = async (barcode: string): Promise<BarcodeSearchResult> => {
  try {
    const response = await apiClient.get<BarcodeSearchResult>(`/products/search`, {
      params: { barcode }
    });
    return response.data;
  } catch (error: any) {
    console.error(`Error searching product by barcode ${barcode}:`, error);
    throw new Error(error.response?.data?.detail || 'Produto não encontrado para este código de barras');
  }
};

const productService = {
  getProducts,
  getProductsCliente,
  getProductById,
  createProduct,
  updateProduct,
  deleteProduct,
  searchProductByBarcode,
};

export default productService;
